import { Loader } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { Link } from "@brexhq/next/src/components/shared/Link";
import { getDateAndTime } from "@brexhq/next/src/utils/dates/formatting";
import { BodyS } from "@brexhq/shared/components/Typography/Components";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { ThemeTextComponents } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";
import envVariables from "@brexhq/shared/utils/env/envVariables";
import { sanityDataset } from "@brexhq/shared/utils/sanity/config";

import {
  nextArchivePageQuery,
  nextIdQuery,
  nextSlugQuery,
} from "../../../src/utils/previews/utils";

const { SANITY_PROJECT_ID } = envVariables;

// token not in bundle on prod
const SANITY_TOKEN =
  process.env.BREX_ENV !== "production"
    ? process.env.SANITY_PREVIEW_TOKEN
    : undefined;

const PreviewBannerWrapper = styled.div(({ theme }) => {
  return {
    position: "fixed",
    bottom: "10px",
    right: "10px",
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borders.radius.default,
    padding: "20px 30px",
    zIndex: 100,
    boxShadow: theme.shadows.floating,
    textAlign: "center",
  };
});

const UpdateWrapper = styled.div(({ theme }) => {
  return {
    position: "absolute",
    bottom: "100%",
    right: "0",
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borders.radius.default,
    padding: "20px 30px",
    margin: "20px 0",
    boxShadow: theme.shadows.floating,
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
});

interface CommonProps {
  isPreviewMode: boolean;
  updatedAt: Date | string;
  archivePageType?: string;
  exitPath?: string;
}

interface SlugProps extends CommonProps {
  id?: never;
  slug: string;
}

interface IDProps extends CommonProps {
  id: string;
  slug?: never;
}

type Props = SlugProps | IDProps;

const PreviewBanner = ({
  isPreviewMode,
  slug,
  id,
  updatedAt,
  archivePageType,
  exitPath,
}: Props) => {
  const [updatingAlert, setUpdatingAlert] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const router = useRouter();
  const { colors } = useTheme();

  useEffect(() => {
    if (isPreviewMode) {
      setShowBanner(true);
    }
  }, [isPreviewMode]);

  useEffect(() => {
    if (!(isPreviewMode && SANITY_TOKEN)) {
      return;
    }

    let pageQuery = "";
    if (archivePageType) {
      pageQuery = nextArchivePageQuery(archivePageType);
    } else if (slug) {
      pageQuery = nextSlugQuery(slug);
    } else if (id) {
      pageQuery = nextIdQuery(id);
    } else {
      console.error("[PreviewBanner]: slug or id must be provided");
      return;
    }

    let cleanup: (() => void) | undefined;

    // Dynamic import to avoid bundling @sanity/client in main bundle
    import("@sanity/client")
      .then(({ createClient }) => {
        const sanityClient = createClient({
          projectId: SANITY_PROJECT_ID,
          dataset: sanityDataset,
          token: SANITY_TOKEN,
          useCdn: false,
          apiVersion: "2021-03-31",
        });

        const subscription = sanityClient
          .listen(pageQuery, {}, { includeResult: false })
          .subscribe(() => {
            console.log("document updated - requerying");
            // should alert
            const rdrUrl = new URL(window.location.href);
            router.replace(rdrUrl);
            setUpdatingAlert(true);
          });

        cleanup = () => subscription.unsubscribe();
      })
      .catch((error) => {
        console.error("Failed to load Sanity client:", error);
      });

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [router, slug, id, archivePageType, isPreviewMode]);

  // when updatedAt has been changed, that means that the data has been refreshed
  // and we can hide the "updating now" alert
  useEffect(() => {
    setUpdatingAlert(false);
  }, [updatedAt]);

  useEffect(() => {
    if (isPreviewMode) {
      const rdrUrl = new URL(window.location.href);
      const searchParams = rdrUrl.searchParams;
      if (searchParams.has("secret") || searchParams.has("previewslug")) {
        rdrUrl.searchParams.delete("secret");
        rdrUrl.searchParams.delete("previewslug");
        router.replace(rdrUrl, undefined, {
          shallow: true,
        });
      }
    }
  }, [router, isPreviewMode]);

  if (!showBanner) return null;

  return (
    <PreviewBannerWrapper>
      {updatingAlert && (
        <UpdateWrapper>
          <Text
            as="p"
            components={ThemeTextComponents["Body S"]?.components}
            color={colors.content.secondary}
            margin={{
              LRG: ["8px", 0],
              MED: ["8px", 0],
              SML: ["8px", 0],
            }}
          >
            This document has been updated! Refreshing content now...
          </Text>
        </UpdateWrapper>
      )}

      {updatedAt ? (
        <Text
          as="p"
          components={ThemeTextComponents["Body S"]?.components}
          color={colors.content.secondary}
          margin={{
            LRG: [8, 0],
            MED: [8, 0],
            SML: [8, 0],
          }}
        >
          {"Content Last Updated At: "} <br />
          <strong>{getDateAndTime(updatedAt)}</strong>
        </Text>
      ) : null}

      {isExiting ? (
        <>
          <BodyS margin={{ LRG: [0, 0, 12, 0] }}>Exiting</BodyS>
          <Loader />
        </>
      ) : (
        <Link
          onClick={() => setIsExiting(true)}
          label="Exit Preview Mode"
          to={`/api/exit-preview?slug=${exitPath ?? slug}`}
          component="Exit Preview Mode"
          prefetch={false}
        ></Link>
      )}
    </PreviewBannerWrapper>
  );
};
export default PreviewBanner;
